import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  Package,
  FileText,
  Shield,
  MessageSquare,
  Users,
  FileCheck,
  FolderOpen,
  User as UserIcon,
  HelpCircle,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';

export const BottomNav = () => {
  const location = useLocation();
  const { permissions, user } = useAuth();
  const [showMore, setShowMore] = useState(false);

  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'OPERADOR' ||
    user?.role === 'SUPERVISOR' ||
    permissions?.can_view_admin;

  const isSuperAdmin = user?.role === 'SUPERADMIN' || permissions?.can_view_superadmin;

  const clientNav = [
    { name: 'Productos', href: '/products', icon: Package },
    { name: 'Cotizar', href: '/quotes', icon: FileText },
    { name: 'home', href: '/dashboard', isHome: true },
    { name: 'Polizas', href: '/policies', icon: Shield },
    { name: 'Mas', href: '#more', icon: HelpCircle, isMore: true },
  ];

  const adminNav = [
    { name: 'Usuarios', href: '/admin/users', icon: Users },
    { name: 'Polizas', href: '/admin/policies', icon: Shield },
    { name: 'home', href: isSuperAdmin ? '/superadmin' : '/admin', isHome: true },
    { name: 'Docs', href: '/admin/documents', icon: FileCheck },
    { name: 'Mas', href: '#more', icon: HelpCircle, isMore: true },
  ];

  const clientMoreItems = [
    { name: 'Mis Pagos', href: '/payments', icon: Wallet },
    { name: 'Documentos', href: '/documents', icon: FolderOpen },
    { name: 'Solicitudes', href: '/requests', icon: MessageSquare },
    { name: 'Mi Perfil', href: '/profile', icon: UserIcon },
  ];

  const adminMoreItems = [
    { name: 'Cotizaciones', href: '/admin/quotes', icon: FileText },
    { name: 'Solicitudes', href: '/admin/requests', icon: MessageSquare },
    { name: 'Mi Perfil', href: '/profile', icon: UserIcon },
  ];

  const nav = isAdmin ? adminNav : clientNav;
  const moreItems = isAdmin ? adminMoreItems : clientMoreItems;

  return (
    <>
      {/* More overlay */}
      {showMore && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setShowMore(false)}
          data-testid="bottomnav-overlay"
        />
      )}

      {/* More panel */}
      {showMore && (
        <div className="fixed bottom-[var(--bottomnav-height)] left-0 right-0 z-50 bg-white border-t border-border rounded-t-3xl shadow-2xl p-5 md:hidden" data-testid="bottomnav-more-panel"
          style={{ animation: 'slideUp 0.25s ease-out' }}>
          <div className="w-12 h-1.5 bg-border rounded-full mx-auto mb-5" />
          <div className="grid grid-cols-3 gap-3">
            {moreItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setShowMore(false)}
                  data-testid={`bottomnav-more-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={cn(
                    'flex flex-col items-center gap-2 py-4 px-2 rounded-2xl text-xs font-semibold active:scale-90 transition-all',
                    isActive
                      ? 'bg-[#052f6b] text-white shadow-lg'
                      : 'text-foreground/80 bg-slate-50 active:bg-slate-100'
                  )}
                >
                  <Icon className="w-6 h-6" strokeWidth={1.8} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
        style={{ height: 'var(--bottomnav-height)' }}
        data-testid="bottom-nav"
      >
        {/* Background with blur */}
        <div className="absolute inset-0 bg-white/95 backdrop-blur-lg border-t border-slate-200/80" />
        
        <div className="relative flex items-center justify-around h-full max-w-lg mx-auto px-2">
          {nav.map((item) => {
              if (item.isHome) {
              // Central elevated Home button with real isologo
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key="home"
                  to={item.href}
                  onClick={() => setShowMore(false)}
                  data-testid="bottomnav-home"
                  className="relative -mt-5 active:scale-90 transition-transform"
                >
                  <div className={cn(
                    'w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all overflow-hidden',
                    isActive
                      ? 'bg-gradient-to-br from-[#052f6b] to-[#279ddc] shadow-[#279ddc]/30'
                      : 'bg-[#052f6b] shadow-[#052f6b]/20'
                  )}>
                    <img src="/isologo-blanco.png" alt="Home" className="w-9 h-9 object-contain" />
                  </div>
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-1 rounded-full bg-[#09d7d7]" />
                  )}
                </Link>
              );
            }

            const Icon = item.icon;
            const isActive = item.isMore
              ? showMore
              : location.pathname === item.href ||
                (item.href !== '/dashboard' && item.href !== '/admin' && item.href !== '/superadmin' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.name}
                to={item.isMore ? '#' : item.href}
                onClick={(e) => {
                  if (item.isMore) {
                    e.preventDefault();
                    setShowMore(!showMore);
                  } else {
                    setShowMore(false);
                  }
                }}
                data-testid={`bottomnav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 active:scale-90 transition-all',
                  isActive ? 'text-[#052f6b]' : 'text-slate-400'
                )}
              >
                <div className={cn(
                  'rounded-xl px-3 py-1.5 transition-all',
                  isActive && 'bg-[#279ddc]/10'
                )}>
                  <Icon className="w-6 h-6" strokeWidth={isActive ? 2.2 : 1.5} />
                </div>
                <span className={cn('text-[10px] leading-tight', isActive ? 'font-bold' : 'font-medium')}>
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};
